import React, { useEffect, useState, useMemo } from "react";
import axiosInstance from "../../../../services/axiosInstance";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ThemeProvider,
  createTheme,
  CssBaseline,
  TablePagination,
  Stack,
  TextField,
  InputAdornment,
  Skeleton,
  Grid,
  Button,
  Portal,
  ClickAwayListener,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Search,
  Clock,
  Phone,
  AlertTriangle,
  Download,
  Calendar as CalendarIcon,
  User,
  Building,
  Mail,
  Filter,
  Target,
  ExternalLink,
  Zap,
  CheckCircle2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import * as XLSX from "xlsx";
import SideNavbar from "../../../../components/sideNavbar";
import apiEndPointsConfig from "../../../../services/apiEndPoints";

const theme = createTheme({
  palette: {
    primary: { main: "#6366F1" },
    secondary: { main: "#8B5CF6" },
    warning: { main: "#F59E0B" },
    error: { main: "#EF4444" },
    background: { default: "#F8FAFC" },
  },
  typography: {
    fontFamily: "'Inter', 'Roboto', sans-serif",
    h5: { fontWeight: 800 },
    h6: { fontWeight: 800 },
    subtitle1: { fontWeight: 600 },
    body2: { fontSize: "0.875rem" },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 20px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 20,
        },
      },
    },
  },
});

const GlassPaper = ({ children, sx, ...props }) => (
  <Paper
    elevation={0}
    sx={{
      background: "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(20px)",
      WebkitBackdropFilter: "blur(20px)",
      border: "1px solid rgba(255, 255, 255, 0.3)",
      boxShadow: "0 10px 40px -10px rgba(0,0,0,0.05)",
      ...sx,
    }}
    {...props}
  >
    {children}
  </Paper>
);

const MetricCard = ({ title, value, subtitle, icon: Icon, color, loading, gradient }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.02 }}
    transition={{ duration: 0.4 }}
  >
    <GlassPaper
      sx={{
        p: 1.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        borderLeft: `3px solid ${color}`,
      }}
    >
      {loading ? (
        <Stack spacing={0.5}>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="rectangular" height={25} width="40%" borderRadius={1} />
        </Stack>
      ) : (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" sx={{ color: "#64748B", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.6rem" }}>
              {title}
            </Typography>
            <Box sx={{ p: 0.5, borderRadius: 1.5, bgcolor: `${color}10`, color: color }}>
              <Icon size={14} />
            </Box>
          </Stack>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#1E293B", letterSpacing: -0.5, fontSize: "1.1rem" }}>
              {value}
            </Typography>
            <Typography variant="caption" sx={{ color: "#94A3B8", fontWeight: 500, fontSize: "0.6rem" }}>
              {subtitle}
            </Typography>
          </Box>
        </>
      )}
    </GlassPaper>
  </motion.div>
);

const UrgencyBadge = ({ createdAt }) => {
  const hours = (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60);
  let color = "#10B981";
  let label = "Standard";
  let bg = "#10B98115";

  if (hours > 48) {
    color = "#EF4444";
    label = "Critical";
    bg = "#EF444415";
  } else if (hours > 24) {
    color = "#F59E0B";
    label = "Urgent";
    bg = "#F59E0B15";
  }

  return (
    <Chip
      label={`${label} • ${Math.floor(hours)}h`}
      size="small"
      sx={{
        bgcolor: bg,
        color: color,
        fontWeight: 800,
        fontSize: "0.65rem",
        borderRadius: 1.5,
        height: 22,
        border: `1px solid ${color}30`
      }}
    />
  );
};

const UnreachedLeadsReport = () => {
  const [data, setData] = useState({
    details: [],
    summary: { totalUnreached: 0, avgDelayHrs: 0, oldestLead: null, trend: [] }
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [calendarVisible, setCalendarVisible] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {
        startDate: selectedDateRange ? selectedDateRange[0].toISOString() : null,
        endDate: selectedDateRange ? selectedDateRange[1].toISOString() : null,
      };
      const res = await axiosInstance.get(`${apiEndPointsConfig.getUnreachedReport}/me`, { params });
      setData(res.data);
    } catch (err) {
      console.error("Error fetching report:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDateRange]);

  const filteredDetails = useMemo(() => {
    return data.details.filter(
      (lead) =>
        lead.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.phone?.includes(searchQuery) ||
        lead.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        lead.companyName?.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [data.details, searchQuery]);

  const paginatedData = filteredDetails.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleExport = () => {
    const exportData = filteredDetails.map((lead) => ({
      "Lead Name": lead.name,
      "Company": lead.companyName,
      "Phone": lead.phone,
      "Email": lead.email,
      "Created At": new Date(lead.createdAt).toLocaleString(),
      "Manager": lead.accountManager,
      "Source": lead.source,
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Unreached");
    XLSX.writeFile(wb, `UnreachedLeads_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  return (
    <Box sx={{ ml: { xs: 0, md: "280px" }, minHeight: "100vh", bgcolor: "white" }}>
      <SideNavbar />
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container maxWidth="xl" sx={{ py: 3 }}>

          {/* Header & Controls */}
          <Box sx={{ mb: 3 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} lg={7}>
                <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="h6" sx={{ color: "#1E293B", fontWeight: 800 }}>
                      Unreached Leads
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: "#64748B" }}>
                    Leads exceeding the 24-hour window.
                  </Typography>
                </motion.div>
              </Grid>
              <Grid item xs={12} lg={5}>
                <Stack direction="row" spacing={2} justifyContent={{ xs: "flex-start", lg: "flex-end" }}>
                  <Button
                    variant="outlined"
                    startIcon={<Download size={20} />}
                    onClick={handleExport}
                    sx={{ borderColor: "#E2E8F0", color: "#475569", "&:hover": { bgcolor: "#fff" } }}
                  >
                    Export
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CalendarIcon size={20} />}
                    onClick={() => setCalendarVisible(!calendarVisible)}
                    sx={{ boxShadow: "0 10px 20px -5px rgba(99, 102, 241, 0.4)" }}
                  >
                    {selectedDateRange ? "Custom Range" : "Select Period"}
                  </Button>

                  <AnimatePresence>
                    {calendarVisible && (
                      <Portal>
                        <ClickAwayListener onClickAway={() => setCalendarVisible(false)}>
                          <Box sx={{
                            position: "fixed", top: "180px", right: "80px", zIndex: 1300,
                            boxShadow: "0 30px 60px rgba(0,0,0,0.2)", borderRadius: 5,
                            bgcolor: "#fff", p: 2, border: "1px solid #E2E8F0"
                          }}>
                            <Calendar
                              selectRange
                              onChange={(r) => { setSelectedDateRange(r); setCalendarVisible(false); }}
                              value={selectedDateRange}
                              maxDate={new Date()}
                            />
                          </Box>
                        </ClickAwayListener>
                      </Portal>
                    )}
                  </AnimatePresence>
                </Stack>
              </Grid>
            </Grid>
          </Box>

          {/* Metrics Grid */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Total Stale Leads"
                value={data.summary.totalUnreached}
                subtitle="Awaiting initial outreach"
                icon={AlertTriangle}
                color="#EF4444"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg. Delay Time"
                value={`${data.summary.avgDelayHrs}h`}
                subtitle="Average time since creation"
                icon={Clock}
                color="#F59E0B"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Oldest Lead"
                value={data.summary.oldestLead ? `${Math.floor((Date.now() - new Date(data.summary.oldestLead).getTime()) / (1000 * 60 * 60))}h` : "—"}
                subtitle="Maximum pending durationIcon"
                icon={Zap}
                color="#8B5CF6"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Manager Coverage"
                value={`${((data.details.filter(l => l.accountManager !== "Unassigned").length / (data.details.length || 1)) * 100).toFixed(0)}%`}
                subtitle="Leads with assigned owners"
                icon={User}
                color="#6366F1"
                loading={loading}
              />
            </Grid>
          </Grid>

          {/* Trend Analysis Section - REMOVED PER USER REQUEST */}

          {/* Main Table Section */}
          <GlassPaper sx={{ overflow: "hidden" }}>
            <Stack direction="row" sx={{ p: 3, bgcolor: "rgba(248, 250, 252, 0.5)", borderBottom: "1px solid #E2E8F0" }} spacing={3}>
              <TextField
                placeholder="Deep search across leads, companies & owners..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={20} color="#6366F1" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 300,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 4,
                    bgcolor: "#fff",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                    "&:hover fieldset": { borderColor: "#6366F1" }
                  }
                }}
              />
            </Stack>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                    <TableCell sx={{ py: 3 }}><Typography variant="caption" sx={{ fontWeight: 800, color: "#64748B" }}>LEAD IDENTITY</Typography></TableCell>
                    <TableCell><Typography variant="caption" sx={{ fontWeight: 800, color: "#64748B" }}>CONNECTIVITY</Typography></TableCell>
                    <TableCell><Typography variant="caption" sx={{ fontWeight: 800, color: "#64748B" }}>SLA STATUS</Typography></TableCell>
                    <TableCell><Typography variant="caption" sx={{ fontWeight: 800, color: "#64748B" }}>OWNERSHIP</Typography></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    [...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={5} sx={{ py: 3 }}><Skeleton variant="rectangular" height={60} borderRadius={2} /></TableCell>
                      </TableRow>
                    ))
                  ) : paginatedData.length > 0 ? (
                    paginatedData.map((lead, index) => (
                      <motion.tr
                        key={lead.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        style={{ borderBottom: "1px solid #F1F5F9" }}
                      >
                        <TableCell sx={{ py: 2.5 }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: "#6366F110", color: "#6366F1", fontWeight: 700, borderRadius: 3 }}>
                              {lead.name?.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>{lead.name}</Typography>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Building size={12} color="#94A3B8" />
                                <Typography variant="caption" color="text.secondary">{lead.companyName || "Personal Lead"}</Typography>
                              </Stack>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={lead.email || "No email"}>
                            <Stack spacing={0.5}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Phone size={14} color="#10B981" />
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>{lead.phone}</Typography>
                              </Stack>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Mail size={12} color="#94A3B8" />
                                <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>{lead.email?.split("@")[0]}...</Typography>
                              </Stack>
                            </Stack>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <UrgencyBadge createdAt={lead.createdAt} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: lead.accountManager === "Unassigned" ? "#EF4444" : "#10B981" }}>
                              {lead.accountManager?.charAt(0)}
                            </Avatar>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: "#475569" }}>{lead.accountManager}</Typography>
                          </Stack>
                        </TableCell>
                      </motion.tr>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 10 }}>
                        <Stack alignItems="center" spacing={2} sx={{ opacity: 0.5 }}>
                          <CheckCircle2 size={64} color="#10B981" />
                          <Typography variant="h6">Pipeline Clear</Typography>
                          <Typography variant="body2">No unreached leads detected in this timeframe.</Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ p: 3, display: "flex", justifyContent: "flex-end" }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredDetails.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </Box>
          </GlassPaper>
        </Container>
      </ThemeProvider>
    </Box>
  );
};

export default UnreachedLeadsReport;
